import type { ProductsContent } from '@mawkaa/types';
import { ShoppingBag } from 'lucide-react';

interface ProductsBlockProps {
  content: ProductsContent;
  isEditing?: boolean;
}

export function ProductsBlock({ content, isEditing }: ProductsBlockProps) {
  const { title, subtitle, products = [], columns = 3 } = content;

  const gridCols = {
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
  };

  if (products.length === 0) {
    return (
      <section className="px-4 py-16">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold">{title || 'Nos Produits'}</h2>
            {subtitle && <p className="mt-4 text-lg text-muted-foreground">{subtitle}</p>}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-white rounded-xl overflow-hidden shadow-sm border">
                <div className="aspect-square bg-gray-100 flex items-center justify-center">
                  <ShoppingBag className="w-12 h-12 text-gray-300" />
                </div>
                <div className="p-4">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                  <div className="h-3 bg-gray-100 rounded w-1/2 mb-4" />
                  <div className="flex justify-between items-center">
                    <div className="h-5 bg-gray-200 rounded w-16" />
                    <div className="h-8 bg-gray-100 rounded w-24" />
                  </div>
                </div>
              </div>
            ))}
          </div>
          {isEditing && (
            <p className="text-center text-muted-foreground mt-8">
              Ajoutez vos produits
            </p>
          )}
        </div>
      </section>
    );
  }

  return (
    <section className="px-4 py-16">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold">{title}</h2>
          {subtitle && <p className="mt-4 text-lg text-muted-foreground">{subtitle}</p>}
        </div>
        <div className={`grid ${gridCols[columns as keyof typeof gridCols] || gridCols[3]} gap-6`}>
          {products.map((product, index) => (
            <div key={index} className="bg-white rounded-xl overflow-hidden shadow-sm border group">
              <div className="aspect-square overflow-hidden relative">
                {product.image ? (
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                ) : (
                  <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                    <ShoppingBag className="w-12 h-12 text-gray-300" />
                  </div>
                )}
                {product.badge && (
                  <span className="absolute top-3 left-3 bg-primary text-white text-xs px-2 py-1 rounded">
                    {product.badge}
                  </span>
                )}
              </div>
              <div className="p-4">
                <h3 className="font-medium text-lg">{product.name}</h3>
                {product.description && (
                  <p className="text-muted-foreground text-sm mt-1 line-clamp-2">{product.description}</p>
                )}
                <div className="flex justify-between items-center mt-4">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-lg">{product.price}</span>
                    {product.originalPrice && (
                      <span className="text-muted-foreground line-through text-sm">{product.originalPrice}</span>
                    )}
                  </div>
                  {product.buttonText && (
                    <a
                      href={product.buttonUrl || '#'}
                      className="px-4 py-2 bg-primary text-white text-sm rounded-lg hover:bg-primary/90 transition-colors"
                    >
                      {product.buttonText}
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
